import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Lock } from 'lucide-react';

const UNLOCK_KEY = 'eqs_site_unlocked';

// Temporary lock screen while elquesabe.com.sv is public but the product is
// still in development. Frontend-only check against a build-time env var —
// a soft gate to keep casual visitors out, not real access control (the
// password ships inside the built JS bundle, and the backend API is
// reachable directly regardless of this gate).
//
// Fails closed on purpose: if REACT_APP_SITE_PASSWORD is unset or empty, no
// input can ever match, so a missed CI secret never results in an
// accidentally-open production site.
//
// Note: rotating the password (new env var + redeploy) does not
// retroactively re-lock browsers that already unlocked — the unlock flag
// lives in their localStorage indefinitely. Remove this component entirely
// once the product is ready to go public.
const SiteGate = ({ children }) => {
  const { t } = useTranslation();
  const [unlocked, setUnlocked] = useState(
    () => localStorage.getItem(UNLOCK_KEY) === 'true'
  );
  const [password, setPassword] = useState('');
  const [error, setError] = useState(false);

  if (unlocked) {
    return children;
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    const sitePassword = process.env.REACT_APP_SITE_PASSWORD;
    if (sitePassword && password === sitePassword) {
      localStorage.setItem(UNLOCK_KEY, 'true');
      setUnlocked(true);
      setError(false);
    } else {
      setError(true);
      toast.error(t('comingSoon.error'));
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: 'linear-gradient(to bottom right, hsl(215 100% 95%), hsl(0 0% 100%), hsl(48 100% 95%))' }}
    >
      <div className="w-full max-w-sm text-center">
        <h1
          className="text-3xl font-bold mb-2"
          style={{ fontFamily: 'Outfit', color: 'hsl(215 100% 34%)' }}
        >
          elQueSabe
        </h1>
        <div className="flex justify-center mb-4">
          <div className="rounded-full bg-primary/10 p-3">
            <Lock className="w-6 h-6" style={{ color: 'hsl(215 100% 34%)' }} />
          </div>
        </div>
        <h2
          className="text-xl font-semibold mb-2"
          style={{ fontFamily: 'Outfit', color: 'hsl(215 25% 12%)' }}
        >
          {t('comingSoon.title')}
        </h2>
        <p className="text-muted-foreground mb-6">
          {t('comingSoon.subtitle')}
        </p>
        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <Input
            type="password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setError(false);
            }}
            placeholder={t('comingSoon.password_placeholder')}
            autoFocus
            data-testid="site-gate-password-input"
          />
          {error && (
            <p className="text-sm text-destructive" data-testid="site-gate-error">
              {t('comingSoon.error')}
            </p>
          )}
          <Button type="submit" className="rounded-full" data-testid="site-gate-submit-button">
            {t('comingSoon.submit')}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default SiteGate;
